import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Info } from 'lucide-react';
import trainingApi from './api';
import surveyApi from './surveys/surveyApi';
import SlideViewer from './components/SlideViewer';
import Quiz from './Quiz';
import { useTrainingLang } from './i18n/TrainingLangContext';
import { nativeLabelFor } from './i18n/strings';

const TrainingViewer = () => {
  const { trainingId } = useParams();
  const navigate = useNavigate();
  const { language, t } = useTrainingLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stage, setStage] = useState('slides'); // 'slides' | 'quiz'
  const [index, setIndex] = useState(0);
  const [attemptId, setAttemptId] = useState(null);
  const [starting, setStarting] = useState(false);
  const [relatedSurvey, setRelatedSurvey] = useState(null);

  // Refetching on language change swaps the deck and the quiz text in place. The
  // slide index is deliberately preserved: switching language should not send someone
  // back to slide 1, and every language version of a deck has the same slide count.
  const fetchTraining = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await trainingApi.getTraining(trainingId, language);
      setData(res);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [trainingId, language]);

  useEffect(() => { fetchTraining(); }, [fetchTraining]);

  // Optional: a survey linked to this training, surfaced on the results screen.
  // Surveys are non-essential to the training flow, so any failure is ignored.
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const r = await surveyApi.surveyForTraining(trainingId);
        if (active && r && r.survey && r.can_respond) setRelatedSurvey(r);
      } catch (_) { /* surveys optional */ }
    })();
    return () => { active = false; };
  }, [trainingId]);

  const beginQuiz = async () => {
    setStarting(true);
    setError('');
    try {
      // The attempt records the language it was taken in, which is what the admin
      // language breakdown reports on.
      const { attempt_id } = await trainingApi.startAttempt(trainingId, language);
      setAttemptId(attempt_id);
      setStage('quiz');
    } catch (e) {
      setError(e.message);
    } finally {
      setStarting(false);
    }
  };

  const handleRetry = () => {
    setAttemptId(null);
    setStage('slides');
    setIndex(0);
  };

  const handleExit = () => {
    navigate('/tiles/ksp_training/dashboard');
  };

  const slideUrlForLanguage = useCallback(
    (id, n) => trainingApi.slideUrl(id, n, language),
    [language]
  );

  // Only blank the screen on the very first load. A language switch refetches while
  // the current content stays mounted — otherwise the quiz would unmount mid-attempt
  // and the answers already selected would be lost.
  if (loading && !data) return <div className="p-8 text-slate-300">{t('loading')}</div>;
  if (error && !data) return <div className="p-8 text-rose-300">{error}</div>;
  if (!data) return null;

  const { training, slides, questions, slide_language: slideLanguage } = data;

  if (stage === 'quiz' && attemptId) {
    return (
      <div className="min-h-screen bg-slate-900">
        <Quiz
          training={training}
          questions={questions}
          attemptId={attemptId}
          relatedSurvey={relatedSurvey}
          onRetry={() => { handleRetry(); beginQuiz(); }}
          onExit={handleExit}
        />
      </div>
    );
  }

  // Synthetic pre-quiz slide appended after the last real slide whenever a quiz exists.
  // SlideViewer renders this in place of the slide image when the user swipes/clicks past the end.
  const preQuizSlide = slides.length > 0 && questions.length > 0 ? (
    <div className="flex flex-col items-center justify-center text-center max-w-xl">
      <h2 className="text-3xl font-bold text-slate-100 mb-3">{t('readyTitle')}</h2>
      <p className="text-slate-400 mb-8">
        {t('readyBody', { pct: training.pass_threshold_pct })}
      </p>
      <button
        type="button"
        onClick={beginQuiz}
        disabled={starting}
        className="px-10 py-5 text-xl rounded-lg bg-amber-600 hover:bg-amber-500 text-white shadow-lg disabled:opacity-50"
      >
        {starting ? t('starting') : t('startQuiz')}
      </button>
    </div>
  ) : null;

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col">
      <div className="flex items-center justify-between p-4 border-b border-slate-700 bg-slate-800">
        <button
          type="button"
          onClick={handleExit}
          className="flex items-center gap-1 text-sm text-slate-300 hover:text-white"
        >
          <ArrowLeft className="w-4 h-4" /> {t('back')}
        </button>
        <h1 className="text-lg font-semibold text-slate-100">{training.title}</h1>
        <div className="text-xs text-slate-400">
          {t('passLabel', { pct: training.pass_threshold_pct })}
        </div>
      </div>

      {/* The deck can fall back to English independently of the quiz text — a language
          may have reviewed questions but no translated PowerPoint yet. */}
      {slideLanguage && slideLanguage !== language && (
        <div className="px-4 py-2 bg-amber-900/20 border-b border-amber-800/60 text-amber-200 text-xs flex items-center justify-center gap-1.5">
          <Info className="w-3.5 h-3.5" />
          {t('deckFallbackNotice', { language: nativeLabelFor(language) })}
        </div>
      )}

      {slides.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center p-8 text-slate-300">
          <p className="mb-4">{t('noSlides')}</p>
          {questions.length > 0 && (
            <button
              type="button"
              onClick={beginQuiz}
              disabled={starting}
              className="px-4 py-2 rounded bg-amber-600 hover:bg-amber-500 text-white"
            >
              {starting ? t('starting') : t('skipToQuiz')}
            </button>
          )}
        </div>
      ) : (
        <SlideViewer
          trainingId={training.id}
          slideNumbers={slides}
          index={index}
          onIndexChange={setIndex}
          slideUrl={slideUrlForLanguage}
          extraFinalSlide={preQuizSlide}
          variantKey={slideLanguage || language}
        />
      )}
    </div>
  );
};

export default TrainingViewer;
